// ShowOnboardingForm.jsx
// Save accompanying styles into: ShowOnboardingForm.css

import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getOnboarding } from '../../services/onboardingService';
import { Star, ArrowLeft } from 'lucide-react';
import './ShowOnboardingHotel.css';

const ShowOnboardingForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const showId = searchParams.get('show');

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const carouselRef = useRef(null);

  useEffect(() => {
    if (!showId) return setError('No show id provided in query param `?show=`');

    let mounted = true;
    setLoading(true);
    getOnboarding(showId)
      .then((res) => {
        if (!mounted) return;
        if (res && res.data && res.data.record) {
          setRecord(res.data.record);
        } else {
          setError('Record not found');
        }
      })
      .catch((err) => {
        setError(err.message || 'Failed to fetch');
      })
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [showId]);

  // Autoplay carousel
  useEffect(() => {
    if (!record) return;
    const imgs = [record.image, ...(record.gallery || []).flatMap((g) => g.url || [])].filter(Boolean);
    if (imgs.length <= 1) return;

    const id = setInterval(() => {
      setActiveImage((s) => (s + 1) % imgs.length);
    }, 3500);

    return () => clearInterval(id);
  }, [record]);

  const openMap = () => {
    if (!record?.location?.coordinates) return;
    const [lng, lat] = record.location.coordinates;
    const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    window.open(url, '_blank');
  };

  const imgs = record
    ? [record.image, ...(record.gallery || []).flatMap((g) => g.url || [])].filter(Boolean)
    : [];

  if (loading) {
    return (
      <div className="onboarding-root">
        <div className="card pulse">
          <div className="skeleton-image" />
          <div className="skeleton-line" style={{ width: '60%' }} />
          <div className="skeleton-line" style={{ width: '40%' }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="onboarding-root">
        <div className="card error-card">
          <h3>Error</h3>
          <p>{error}</p>
          <button className="btn" onClick={() => navigate(-1)}>Go back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-root">
      <button className="back-btn" onClick={() => navigate(-1)} aria-label="Go back">
        <ArrowLeft size={20} />
        <span>Back</span>
      </button>
      <div className="card animated-card">
        <div className="media" ref={carouselRef}>
          {imgs.length ? (
            <>
              <img
                src={imgs[activeImage]}
                alt={`slide-${activeImage}`}
                className="hero-image fade-in"
                onClick={() => setActiveImage((s) => (s + 1) % imgs.length)}
              />

              <div className="thumbs">
                {imgs.map((u, i) => (
                  <button
                    key={u + i}
                    className={`thumb ${i === activeImage ? 'active' : ''}`}
                    onClick={() => setActiveImage(i)}
                    aria-label={`Show image ${i + 1}`}
                    style={{ backgroundImage: `url(${u})` }}
                  />
                ))}
              </div>

              <div className="badge">
                {record.status ? 'Approved' : 'Pending'}
              </div>
            </>
          ) : (
            <div className="no-image">No image</div>
          )}
        </div>

        <div className="content">
          <div className="title-row">
            <h1 className="title">{record.name}</h1>
            <div className="rating" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Star size={18} fill="#fbbf24" color="#fbbf24" />
              {record.rating ?? '-'}
            </div>
          </div>

          <div className="meta">
            <div>
              <strong>Owner:</strong> {record.user?.name} • {record.user?.email}
            </div>
            <div>
              <strong>Address:</strong> {record.address?.street}, {record.address?.city}, {record.address?.state}
            </div>
          </div>

          <p className="short-desc">{record.short_desc || record.description}</p>

          <div className="amenities">
            <h4>Amenities</h4>
            <div className="amenity-list">
              {record.amenities?.map((a) => (
                <div key={a._id} className="amenity-item" title={a.name}>
                  <img src={a.icon} alt={a.name} />
                  <span>{a.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="tags-row">
            {record.tags?.map((t, i) => (
              <span key={t + i} className="tag">{t.trim()}</span>
            ))}
          </div>

          <div className="actions">
            <button className="btn" onClick={openMap}>Open in Maps</button>
            
          </div>

          <div className="details-accordion">
            <details>
              <summary>Full description & policies</summary>
              <div className="accordion-body">
                <p>{record.description}</p>
                <p><strong>Rules:</strong> {record.rulesPolicies}</p>
                <p><strong>Check-in:</strong> {record.check_in} • <strong>Check-out:</strong> {record.check_out}</p>
                <p><strong>Created:</strong> {new Date(record.createdAt).toLocaleString()}</p>
              </div>
            </details>
          </div>

          {/* Owner Details Section */}
          {record.hotelOwnerDetails && (
            <div className="details-accordion">
              <details>
                <summary>Hotel Owner Details</summary>
                <div className="accordion-body owner-details">
                  {record.hotelOwnerDetails.ownerName && (
                    <p><strong>Owner Name:</strong> {record.hotelOwnerDetails.ownerName}</p>
                  )}
                  {record.hotelOwnerDetails.ownerMobile && (
                    <p><strong>Mobile:</strong> {record.hotelOwnerDetails.ownerMobile}</p>
                  )}
                  {record.hotelOwnerDetails.ownerEmail && (
                    <p><strong>Email:</strong> {record.hotelOwnerDetails.ownerEmail}</p>
                  )}
                  {record.hotelOwnerDetails.ownerIdProof && (
                    <p>
                      <strong>Owner ID Proof:</strong>{' '}
                      <a
                        href={record.hotelOwnerDetails.ownerIdProof}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="doc-link"
                      >
                        View Document
                      </a>
                    </p>
                  )}
                  {record.hotelOwnerDetails.hotelRegistrationDoc && (
                    <p>
                      <strong>Registration Document:</strong>{' '}
                      <a
                        href={record.hotelOwnerDetails.hotelRegistrationDoc}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="doc-link"
                      >
                        View Document
                      </a>
                    </p>
                  )}
                </div>
              </details>
            </div>
          )}

        </div>
      </div>

    </div>
  );
}




export default ShowOnboardingForm